import {createSettingsParams, SettingsParamType} from '@wix/tpa-settings'

import {
  booleanMetadata,
  numberSettingMetadata,
  textEnumMetadata,
} from '../../../../../utils/builderMetadata'

export enum ShowSlotsBehavior {
  All = 'all',
  Available = 'available',
}

export const DEFAULT_DISPLAYED_SLOTS_LIMIT = 15
export const MIN_DISPLAYED_SLOTS_LIMIT = 1
export const MAX_DISPLAYED_SLOTS_LIMIT = 100

export const clampDisplayedSlotsLimit = (value: unknown): number => {
  const parsedValue = Number(value)

  if (!Number.isFinite(parsedValue)) {
    return DEFAULT_DISPLAYED_SLOTS_LIMIT
  }

  return Math.min(
    MAX_DISPLAYED_SLOTS_LIMIT,
    Math.max(MIN_DISPLAYED_SLOTS_LIMIT, Math.round(parsedValue)),
  )
}

export type DisplaySettingsParams = {
  showTitle: SettingsParamType.Boolean
  showDescription: SettingsParamType.Boolean
  showAreasDescription: SettingsParamType.Boolean
  showSlotsBehavior: SettingsParamType.String
  limitDisplayedSlots: SettingsParamType.Boolean
  displayedSlotsLimit: SettingsParamType.Number
  showBreadcrumbs: SettingsParamType.Boolean
}

export const DEFAULT_DISPLAY_SETTINGS_PARAMS: Record<keyof DisplaySettingsParams, any> = {
  showTitle: true,
  showDescription: true,
  showAreasDescription: true,
  showSlotsBehavior: ShowSlotsBehavior.All,
  limitDisplayedSlots: true,
  displayedSlotsLimit: DEFAULT_DISPLAYED_SLOTS_LIMIT,
  showBreadcrumbs: true,
}

export const displaySettingsParams = createSettingsParams<DisplaySettingsParams>({
  showTitle: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => DEFAULT_DISPLAY_SETTINGS_PARAMS.showTitle,
    metadata: booleanMetadata(
      DEFAULT_DISPLAY_SETTINGS_PARAMS.showTitle,
      'Shows or hides the page title',
    ),
  },
  showDescription: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => DEFAULT_DISPLAY_SETTINGS_PARAMS.showDescription,
    metadata: booleanMetadata(
      DEFAULT_DISPLAY_SETTINGS_PARAMS.showDescription,
      'Shows or hides the description under the title',
    ),
  },
  showAreasDescription: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => DEFAULT_DISPLAY_SETTINGS_PARAMS.showAreasDescription,
    metadata: booleanMetadata(
      DEFAULT_DISPLAY_SETTINGS_PARAMS.showAreasDescription,
      'Shows or hides the descriptions of the seating areas',
    ),
  },
  showSlotsBehavior: {
    type: SettingsParamType.String,
    getDefaultValue: () => DEFAULT_DISPLAY_SETTINGS_PARAMS.showSlotsBehavior,
    metadata: textEnumMetadata(
      {defaultValue: DEFAULT_DISPLAY_SETTINGS_PARAMS.showSlotsBehavior},
      'Which time slots to show: all = every time slot, available = only available time slots',
    ),
  },
  limitDisplayedSlots: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => DEFAULT_DISPLAY_SETTINGS_PARAMS.limitDisplayedSlots,
    metadata: booleanMetadata(
      DEFAULT_DISPLAY_SETTINGS_PARAMS.limitDisplayedSlots,
      'Limits how many time slots are shown at once',
    ),
  },
  displayedSlotsLimit: {
    type: SettingsParamType.Number,
    getDefaultValue: () => DEFAULT_DISPLAY_SETTINGS_PARAMS.displayedSlotsLimit,
    metadata: numberSettingMetadata(
      DEFAULT_DISPLAY_SETTINGS_PARAMS.displayedSlotsLimit,
      `Maximum number of time slots shown when limitDisplayedSlots is on: ${MIN_DISPLAYED_SLOTS_LIMIT} to ${MAX_DISPLAYED_SLOTS_LIMIT}`,
    ),
  },
  showBreadcrumbs: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => DEFAULT_DISPLAY_SETTINGS_PARAMS.showBreadcrumbs,
    metadata: booleanMetadata(
      DEFAULT_DISPLAY_SETTINGS_PARAMS.showBreadcrumbs,
      'Shows or hides the breadcrumbs at the top of the page',
    ),
  },
})
